// frontend/src/components/AllUserAssignments.js

import React, { useState, useEffect } from 'react';
import { getAllUserAssignments, getCohorts } from '../services/api';
import { stringify } from 'csv-stringify/browser/esm';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { notoSansJPFont } from '../fonts/notosansjp-font.js';
import {
  FileText,
  Download,
  Search,
  ArrowLeft,
  Filter
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';

const AllUserAssignments = () => {
  const [assignments, setAssignments] = useState([]);
  const [filteredAssignments, setFilteredAssignments] = useState([]);
  const [cohorts, setCohorts] = useState([]);

  const [selectedCohort, setSelectedCohort] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const [assignmentsData, cohortsData] = await Promise.all([
          getAllUserAssignments(),
          getCohorts()
        ]);

        // Sort cohorts by name descending (e.g. 21M, 20M...)
        const sortedCohorts = cohortsData.sort((a, b) => {
          if (a.name > b.name) return -1;
          if (a.name < b.name) return 1;
          return 0;
        });

        setCohorts(sortedCohorts);
        if (sortedCohorts.length > 0) {
          setSelectedCohort(sortedCohorts[0].name);
        }

        setAssignments(assignmentsData);
        // Initial filtering will be handled by the other useEffect
      } catch (err) {
        console.error('Failed to fetch data:', err);
        setError('データの取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const lowerTerm = searchTerm.toLowerCase();
    const filtered = assignments.filter(user => {
      // Filter by cohort if selected
      if (selectedCohort === 'others') {
        if (user.cohort_name) return false;
      } else if (selectedCohort && user.cohort_name !== selectedCohort) {
        return false;
      }

      return (
        user.username.toLowerCase().includes(lowerTerm) ||
        Object.values(user.assignments).some(assignment =>
          assignment.institution_name?.toLowerCase().includes(lowerTerm) ||
          assignment.department_name?.toLowerCase().includes(lowerTerm)
        )
      );
    });
    setFilteredAssignments(filtered);
  }, [searchTerm, assignments, selectedCohort]);

  const handleCsvExport = () => {
    const header = ['ユーザー名', 'ターム1', 'ターム2', 'ターム3', 'ターム4', 'ターム5', 'ターム6', 'ターム7'];

    const data = [];
    for (const user of assignments) {
      const row = [user.username];
      for (let i = 1; i <= 7; i++) {
        const term = `term${i}`;
        const assignment = user.assignments[term];
        row.push(`${assignment.institution_name}${assignment.department_name ? ` ${assignment.department_name}` : ''}`);
      }
      data.push(row);
    }

    const BOM = '\uFEFF';
    stringify([header, ...data], { bom: true }, (err, csv) => {
      if (err) {
        console.error('CSV生成エラー:', err);
        return;
      }

      const blob = new Blob([BOM + csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'all_user_assignments.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    });
  };

  const handlePdfExport = () => {
    const doc = new jsPDF('l', 'mm', 'a4');

    doc.addFileToVFS('NotoSansJP-Regular.ttf', notoSansJPFont);
    doc.addFont('NotoSansJP-Regular.ttf', 'NotoSansJP', 'normal');

    const header = [['ユーザー名', 'ターム1', 'ターム2', 'ターム3', 'ターム4', 'ターム5', 'ターム6', 'ターム7']];

    const data = [];
    for (const user of assignments) {
      const row = [user.username];
      for (let i = 1; i <= 7; i++) {
        const term = `term${i}`;
        const assignment = user.assignments[term];
        row.push(`${assignment.institution_name}${assignment.department_name ? ` - ${assignment.department_name}` : ''}`);
      }
      data.push(row);
    }

    doc.autoTable({
      head: header,
      body: data,
      startY: 20,
      styles: {
        font: 'NotoSansJP',
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left',
        valign: 'middle'
      },
      columnStyles: {
        0: { cellWidth: 30 },
        1: { cellWidth: 35 },
        2: { cellWidth: 35 },
        3: { cellWidth: 35 },
        4: { cellWidth: 35 },
        5: { cellWidth: 35 },
        6: { cellWidth: 35 },
        7: { cellWidth: 35 }
      }
    });

    doc.save('all_user_assignments.pdf');
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin-dashboard" className="p-2 -ml-2 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-900 transition-colors">
              <ArrowLeft size={24} />
            </Link>
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Admin Console</p>
              <h1 className="text-2xl font-bold text-gray-900">ALL ASSIGNMENTS</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={handleCsvExport}
              variant="outline"
              className="flex items-center gap-2 bg-white text-gray-700 hover:bg-gray-50"
            >
              <FileText size={16} />
              CSV
            </Button>
            <Button
              onClick={handlePdfExport}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Download size={16} />
              PDF
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-8 space-y-6">
        {/* Filter Card */}
        <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
          {/* Cohort Tabs */}
          {cohorts.length > 0 && (
            <div className="mb-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8" aria-label="Tabs">
                {cohorts.map((cohort) => (
                  <button
                    key={cohort.id}
                    onClick={() => setSelectedCohort(cohort.name)}
                    className={`
                      whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                      ${selectedCohort === cohort.name
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                    `}
                  >
                    {cohort.name}
                  </button>
                ))}
                <button
                  onClick={() => setSelectedCohort('others')}
                  className={`
                    whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${selectedCohort === 'others'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                  `}
                >
                  その他
                </button>
              </nav>
            </div>
          )}

          {/* Search & Filter */}
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative flex-1 max-w-md w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="ユーザー名、病院名、科名で検索..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-gray-300 rounded-lg pl-10 pr-4 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all placeholder:text-gray-400"
              />
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 ml-auto bg-gray-50 px-3 py-1.5 rounded-md border border-gray-200">
              <Filter size={14} />
              <span>{filteredAssignments.length} ユーザーを表示中</span>
            </div>
          </div>
        </section>

        {/* Content */}
        {error ? (
          <div className="p-8 text-center bg-red-50 border border-red-200 rounded-xl text-red-600">
            {error}
          </div>
        ) : isLoading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm"
          >
            <div className="overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-50">
                  <TableRow>
                    <TableHead className="w-[180px] min-w-[150px] font-bold text-gray-700 bg-gray-50 sticky left-0 z-10 border-b border-gray-200">ユーザー名</TableHead>
                    {[...Array(7)].map((_, i) => (
                      <TableHead key={`term-${i + 1}`} className="font-bold text-gray-700 whitespace-nowrap min-w-[200px] border-b border-gray-200">
                        ターム {i + 1}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAssignments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-12 text-gray-500">
                        該当するデータが見つかりませんでした
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAssignments.map((user, idx) => (
                      <TableRow
                        key={user.user_id}
                        className="hover:bg-gray-50 transition-colors group"
                      >
                        <TableCell className="font-medium text-gray-900 sticky left-0 bg-white group-hover:bg-gray-50 transition-colors border-r border-gray-100 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                          {user.username}
                        </TableCell>
                        {[...Array(7)].map((_, i) => {
                          const term = `term${i + 1}`;
                          const assignment = user.assignments[term];
                          return (
                            <TableCell key={`${user.user_id}-${term}`} className="text-gray-600 whitespace-nowrap align-top">
                              {assignment.institution_name ? (
                                <div className="flex flex-col gap-0.5">
                                  {assignment.department_name && (
                                    assignment.has_accommodation && assignment.accommodation === false ? (
                                      <span className="text-red-500 font-bold">
                                        {assignment.department_name}
                                        <br />
                                        <span className="text-xs text-red-500">
                                          ({assignment.institution_name})
                                        </span>
                                      </span>
                                    ) : (
                                      <span>
                                        {assignment.department_name}
                                        <br />
                                        <span className="text-xs text-gray-500">
                                          ({assignment.institution_name})
                                        </span>
                                      </span>
                                    )
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-300 transform scale-150 inline-block ml-2">-</span>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AllUserAssignments;

